import { create } from 'zustand'
import { currentGateView } from './device'

export interface ToastAction {
  label: string
  onClick: () => void
}

/** How long a message stays: long enough to read, and longer when it says something went wrong. */
const STAY = 5000
const STAY_ERROR = 8000

export interface Toast {
  id: number
  message: string
  action?: ToastAction
  /** Something went wrong: it stays longer and offers a way to dismiss it. */
  error?: boolean
  /** ms; 0 keeps the toast until dismissed */
  duration: number
}

export interface ToastOptions {
  action?: ToastAction
  duration?: number
  key?: number
  tone?: 'error'
}

interface ToastState {
  toasts: Toast[]
  show: (message: string, options?: ToastOptions) => number
  dismiss: (id: number) => void
  /** Stops the clock while the toast is under a pointer or holds focus. */
  hold: (id: number) => void
  /** Starts it again once it is left alone. */
  release: (id: number) => void
}

let nextId = 1
const timers = new Map<number, ReturnType<typeof setTimeout>>()

function clear(id: number) {
  const timer = timers.get(id)
  if (timer) clearTimeout(timer)
  timers.delete(id)
}

export const useToasts = create<ToastState>((set, get) => {
  const countdown = (id: number, duration: number) => {
    clear(id)
    if (duration > 0) timers.set(id, setTimeout(() => get().dismiss(id), duration))
  }
  return {
    toasts: [],
    show: (message, options = {}) => {
      const id = options.key ?? nextId++
      const error = options.tone === 'error'
      const toast: Toast = { id, message, action: options.action, error, duration: options.duration ?? (error ? STAY_ERROR : STAY) }
      const replaced = get().toasts.filter((t) => t.id !== id && t.message !== message)
      for (const gone of get().toasts.filter((t) => !replaced.includes(t) && t.id !== id)) clear(gone.id)
      set({ toasts: [...replaced, toast].slice(-3) })
      countdown(id, toast.duration)
      return id
    },
    dismiss: (id) => {
      clear(id)
      set({ toasts: get().toasts.filter((t) => t.id !== id) })
    },
    hold: (id) => clear(id),
    release: (id) => {
      const toast = get().toasts.find((t) => t.id === id)
      if (toast) countdown(id, toast.duration)
    },
  }
})

export function toast(message: string, options?: ToastOptions): number {
  // Behind the device gate the app is hidden; errors from background requests (403 device_not_approved)
  // must not pop up over the waiting screen.
  if (currentGateView() !== 'app') return -1
  return useToasts.getState().show(message, options)
}
